import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useDelayedFlag } from '../../hooks/useDelayedFlag';

export { useDelayedFlag };

/** A single grey placeholder block. Size it with className (h-*, w-*). */
export function SkeletonBlock({ className }: { className?: string }) {
  return <span aria-hidden="true" className={clsx('block animate-pulse rounded bg-slate-200 motion-reduce:animate-none', className)} />;
}

/** Header and form/detail cards for pages whose primary content is not a table. */
export function CardSkeleton({ cards = 2 }: { cards?: number }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t('ui.loadingContent')} className="space-y-4">
      <SkeletonBlock className="h-8 w-48" />
      {Array.from({ length: cards }, (_, index) => (
        <div key={index} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
          <SkeletonBlock className="h-4 w-32" />
          <SkeletonBlock className="h-10 w-full" />
          <SkeletonBlock className="h-10 w-3/4" />
        </div>
      ))}
    </div>
  );
}

export interface SkeletonProps {
  rows?: number;
  /** ms before anything is drawn; fast responses never flash a skeleton. */
  delayMs?: number;
  className?: string;
}

/** Table-ish placeholder for the first load of a list. Renders nothing for the first `delayMs`. */
export default function Skeleton({ rows = 5, delayMs = 300, className }: SkeletonProps) {
  const { t } = useTranslation();
  const visible = useDelayedFlag(true, delayMs);
  if (!visible) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t('ui.loadingContent')}
      className={clsx('overflow-hidden rounded-xl border border-slate-200 bg-white', className)}
    >
      <div className="flex h-10 items-center gap-10 bg-slate-100 px-5">
        <SkeletonBlock className="h-2.5 w-[70px]" />
        <SkeletonBlock className="h-2.5 w-[120px]" />
        <SkeletonBlock className="h-2.5 w-[90px]" />
      </div>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex h-12 items-center gap-10 border-t border-slate-100 px-5">
          <SkeletonBlock className="h-2.5 w-[70px]" />
          <SkeletonBlock className="h-2.5 w-[150px]" />
          <SkeletonBlock className="h-2.5 w-[60px]" />
        </div>
      ))}
    </div>
  );
}
